import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Input, Form, Alert, Spin, Steps, Space, Typography } from 'antd';
import { ShopOutlined, LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../api';
import ShopifyIcon from './Utils/ShopifyIcon';

const { Title, Paragraph, Text } = Typography;

const ConnectShopify = () => {
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const callbackProcessedRef = useRef(false); // 使用ref防止重复处理
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // 防止重复处理回调
    if (callbackProcessedRef.current) {
      
      return;
    }

    // 检查是否是从Shopify回调返回
    const code = searchParams.get('code');
    const shop = searchParams.get('shop');
    const state = searchParams.get('state');
    const hmac = searchParams.get('hmac');
    const host = searchParams.get('host');
    const timestamp = searchParams.get('timestamp');
    
    // 标准OAuth回调 (有code参数)
    if (code && shop) {
      console.log('[FRONTEND] Processing OAuth callback with code:', code.substring(0, 10) + '...');
      callbackProcessedRef.current = true;
      handleOAuthCallback(code, shop, state);
    }
    // 嵌入式应用回调 (有hmac参数)
    else if (hmac && shop && timestamp) {
      
      callbackProcessedRef.current = true;
      handleEmbeddedAppCallback(shop, hmac, host, timestamp);
    }
  }, [searchParams]);

  const handleOAuthCallback = async (code, shop, state) => {
    
    
    // 双重检查，确保不会重复执行
    if (loading) {
      
      return;
    }
    
    setLoading(true);
    setCurrentStep(2);
    
    try {
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/shopify/oauth/callback`, {
        code,
        shop,
        state
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        
        setSuccess('Shopify store connected successfully!');
        setCurrentStep(3);
        
        // Clear URL parameters to prevent duplicate processing on page refresh
        window.history.replaceState({}, '', '/dropshipper/connect-shopify');
        
        setTimeout(() => {
          navigate('/dropshipper/my-stores');
        }, 2000);
      } else {
        throw new Error(response.data.message || 'OAuth callback processing failed');
      }
    } catch (error) {
      
      
      let errorMessage = error.response?.data?.message || error.message || 'OAuth callback processing failed';
      
      // Check if it's an authorization code related error
      if (errorMessage.includes('authorization code') || 
          errorMessage.includes('was already used') || 
          errorMessage.includes('invalid_request')) {
        errorMessage = 'Authorization code has expired, please re-authorize. This is usually caused by page refresh or navigation, please click the "Connect Shopify" button again.';
        setCurrentStep(0); // Reset to initial state
        callbackProcessedRef.current = false; // Allow reprocessing
      } else {
        setCurrentStep(0);
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEmbeddedAppCallback = async (shop, hmac, host, timestamp) => {
    setLoading(true);
    setCurrentStep(1); // 设置为"Shopify 授权"步骤
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/shopify/embedded/callback`, {
        shop,
        hmac,
        host,
        timestamp
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Check if OAuth re-authorization is needed
        if (response.data.requires_oauth && response.data.oauth_url) {
          setSuccess('Verification successful, redirecting to Shopify authorization page...');
          setTimeout(() => {
            window.location.href = response.data.oauth_url;
          }, 1000);
        } else {
          setSuccess('Shopify store connected successfully!');
          setCurrentStep(3);
          setTimeout(() => {
            navigate('/dropshipper/my-stores');
          }, 2000);
        }
      } else {
        throw new Error(response.data.message || 'Embedded app callback processing failed');
      }
    } catch (error) {
      
      setError(error.response?.data?.message || error.message || 'Embedded app callback processing failed');
      setCurrentStep(0);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = () => {
    
    setError('');

    // 直接在新窗口打开Shopify App页面
    const shopifyAppUrl = 'https://www.shopify.com/';
    window.open(shopifyAppUrl, '_blank');
  };

  const steps = [
    {
      title: 'Start'
    },
    {
      title: 'Link Your Store',
      icon: currentStep === 1 ? <LoadingOutlined /> : undefined,
    },
    {
      title: 'Authorization',
      icon: currentStep === 2 ? <LoadingOutlined /> : undefined,
    },
    {
      title: 'Complete',
      icon: success ? <CheckCircleOutlined /> : (error ? <CloseCircleOutlined /> : undefined),
    },
  ];

  const handleBackToStores = () => {
    navigate('/dropshipper/my-stores');
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Card>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <ShopifyIcon style={{ fontSize: 48, marginBottom: 16 }} />
          <Title level={2}>Connect Shopify Store</Title>
          <Paragraph type="secondary">
            Connect your Shopify store to our platform and start your dropshipping business
          </Paragraph>
        </div>

        <Steps current={currentStep} items={steps} style={{ marginBottom: 32 }} />

        {error && (
          <Alert
            message="Connection Failed"
            description={
              <div>
                <p>{error}</p>
                {error.includes('Authorization code has expired') && (
                  <Button 
                    type="primary" 
                    size="small" 
                    onClick={() => {
                      setError('');
                      callbackProcessedRef.current = false;
                      setCurrentStep(0);
                      // Clear URL parameters
                      window.history.replaceState({}, '', '/dropshipper/connect-shopify');
                    }}
                    style={{ marginTop: 8 }}
                  >
                    Reconnect
                  </Button>
                )}
              </div>
            }
            type="error"
            closable
            onClose={() => setError('')}
            style={{ marginBottom: 24 }}
          />
        )}

        {success && (
          <Alert
            message="Connection Successful"
            description={success}
            type="success"
            style={{ marginBottom: 24 }}
          />
        )}

        {currentStep === 0 && (
          <Card size="small">
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Space style={{ marginTop: 24 }}>
                <Button onClick={handleBackToStores}>
                  Back to Store List
                </Button>
                <Button 
                  type="primary" 
                  size="large"
                  loading={loading}
                  onClick={handleConnect}
                  style={{  background: '#f0f9f0', borderColor: '#96bf48', color: '#3e7e27ff' }}
                  icon={<ShopifyIcon />}
                >
                  Connect Shopify Store
                </Button>
              </Space>
            </div>
          </Card>
        )}

        {currentStep === 1 && (
          <Card size="small" title="Shopify Authorization in Progress">
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text>Verifying and redirecting to Shopify authorization page...</Text>
              </div>
            </div>
          </Card>
        )}

        {currentStep === 2 && (
          <Card size="small" title="Processing Authorization">
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text>Processing Shopify authorization information...</Text>
              </div>
            </div>
          </Card>
        )}

        {currentStep === 3 && success && (
          <Card size="small" title="Connection Successful">
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
              <div>
                <Text>Store connected successfully! Redirecting to store management page...</Text>
              </div>
            </div>
          </Card>
        )}

      </Card>
    </div>
  );
};

export default ConnectShopify;
